import { creatorbase } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import { SVGProps } from 'react';
import { assets } from '@round/whitelabel-ui';

const {
    InstagramColor: InstagramIcon,
    TiktokColor: TikTokIcon,
    YoutubeColor: YoutubeIcon,
    InstagramSolid: InstagramSolidIcon,
    TiktokSolid: TikTokSolidIcon,
    YoutubeSolid: YoutubeSolidIcon,
    PayPal: PaypalLogo,
    PayPalShort: PaypalShortLogo,
    EllipsisCircle: OtherIcon,
    UserGroup: UserGroupIcon,
    TipaltiLogo,
    TipaltiShort: TipaltiShortLogo,
} = assets;

export const tooltipCopy = {
    engagementRate: 'Engagement rate is total engagements (likes, comments, shares) divided by total views',
    cpm: 'Cost per mille (CPM) is the cost per 1,000 impressions',
    averageDraftTime: 'The time between when a creator is briefed and when their first draft is submitted',
    responseRate: 'Calculated as the percentage of live posts compared to the number of briefed posts.',
    lastUsed: 'The date when the creator last published a live post for a campaign.',
};

export const platforms: creatorbase.Platform[] = ['tiktok', 'instagram', 'youtube'];

export const platformLabels: Record<creatorbase.Platform, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
};

export const platformIcons: Record<creatorbase.Platform, typeof InstagramIcon> = {
    instagram: InstagramIcon,
    tiktok: TikTokIcon,
    youtube: YoutubeIcon,
};

export const platformSolidIcons: Record<creatorbase.Platform, typeof InstagramSolidIcon> = {
    instagram: InstagramSolidIcon,
    tiktok: TikTokSolidIcon,
    youtube: YoutubeSolidIcon,
};

type PlatformIconProps = {
    platform: creatorbase.Platform;
    isSolid?: boolean;
} & SVGProps<SVGSVGElement>;

export const PlatformIcon = ({ platform, isSolid, className, ...props }: PlatformIconProps) => {
    const Icon = isSolid ? platformSolidIcons[platform] : platformIcons[platform];
    return <Icon width={16} height={16} className={className} aria-hidden {...props} />;
};

export const paymentProviderLabels: Record<creatorbase.PaymentProvider, string> = {
    PAYPAL: 'PayPal',
    TIPALTI: 'Tipalti',
    OTHER: 'Other',
    CREATOR_AGENCY: 'Agency',
};

export const paymentProviderOptions: GenericDropdownOption<creatorbase.PaymentProvider>[] = Object.entries(
    paymentProviderLabels
).map(([value, label]) => ({
    label,
    value: value as creatorbase.PaymentProvider,
}));

export const paymentProviderIcons: Record<creatorbase.PaymentProvider, typeof PaypalLogo> = {
    PAYPAL: PaypalLogo,
    TIPALTI: TipaltiLogo,
    OTHER: OtherIcon,
    CREATOR_AGENCY: UserGroupIcon,
};

export const paymentProviderShortIcons: Record<creatorbase.PaymentProvider, typeof PaypalShortLogo> = {
    PAYPAL: PaypalShortLogo,
    TIPALTI: TipaltiShortLogo,
    OTHER: OtherIcon,
    CREATOR_AGENCY: UserGroupIcon,
};

export const buildProjectUrl = (projectId: number | undefined) => {
    if (!projectId) {
        return '';
    }

    return `${window.location.origin}/c/projects/${projectId}/`;
};

export const buildCampaignUrl = (projectId: number | undefined, campaign: creatorbase.Campaign | null | undefined) => {
    const projectPath = buildProjectUrl(projectId);
    if (!projectPath || !campaign) {
        return '';
    }

    const platform = campaign.platform ? `#${campaign.platform}` : '';
    return `${projectPath}?expanded=${campaign.id}&scrollToCampaign=${campaign.id}${platform}`;
};
